import { ProjectCard } from './ProjectCard'
import {
  COMPANY_TYPE_LABELS,
  MARKET_LABELS,
  PRODUCT_TYPE_LABELS,
} from './portfolioConstants'
import type { GroupBy } from './PortfolioToolbar'
import type { Project, ProjectStats } from '@/lib/api/client'

interface ProjectGroup {
  key: string
  label: string
  items: Project[]
}

interface PortfolioGridProps {
  filtered: Project[]
  groups: ProjectGroup[]
  groupBy: GroupBy
  stats: Record<string, ProjectStats>
  onOpen: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}

export function PortfolioGrid({
  filtered,
  groups,
  groupBy,
  stats,
  onOpen,
  onEdit,
  onDelete,
}: PortfolioGridProps) {
  if (filtered.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-text-muted">
        No projects match the current filters.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.key}>
          {groupBy !== 'none' && (
            <div className="flex items-baseline gap-3 pb-2 mb-3 border-b border-border-subtle">
              <h2 className="font-display text-[13px] font-semibold uppercase tracking-wider text-text">
                {group.label}
              </h2>
              <span className="text-[12px] text-text-muted">
                {group.items.length} {group.items.length === 1 ? 'project' : 'projects'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.items.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                stats={stats[project.id]}
                onOpen={() => onOpen(project.id)}
                onEdit={() => onEdit(project.id)}
                onDelete={() => onDelete(project.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function groupProjects(
  projects: Project[],
  groupBy: GroupBy
): ProjectGroup[] {
  if (groupBy === 'none') return [{ key: 'all', label: '', items: projects }]
  const groups = new Map<string, Project[]>()
  for (const p of projects) {
    const raw =
      groupBy === 'companyType' ? p.companyType : groupBy === 'market' ? p.market : p.productType
    const key = (raw as string) || '__unset__'
    const arr = groups.get(key) ?? []
    arr.push(p)
    groups.set(key, arr)
  }
  const labelFor = (key: string): string => {
    if (key === '__unset__') return 'Unspecified'
    if (groupBy === 'companyType')
      return COMPANY_TYPE_LABELS[key as keyof typeof COMPANY_TYPE_LABELS] ?? key
    if (groupBy === 'market')
      return MARKET_LABELS[key as keyof typeof MARKET_LABELS] ?? key
    return PRODUCT_TYPE_LABELS[key as keyof typeof PRODUCT_TYPE_LABELS] ?? key
  }
  return Array.from(groups.entries()).map(([key, items]) => ({
    key,
    label: labelFor(key),
    items,
  }))
}
